import React, {FC, KeyboardEvent, useEffect, useRef, useState} from 'react'
import {DropdownTheme, TextFieldTheme} from 'wix-ui-tpa/cssVars'
import {useSettings} from '@wix/tpa-settings/react'

import {Dropdown} from '../Dropdown'
import {LayoutSize} from '../LayoutSizeProvider/types'
import {AreaOption, getAreaDropdownOptions} from '../../utils/areas'
import {AreasStyle} from '../Settings/Areas/types'

import {classes, st} from './styles.st.css'

const alignmentClasses: Record<string, string> = {
  left: classes.left,
  center: classes.center,
  right: classes.right,
}

const DESCRIPTION_CARD_MIN_WIDTH = 240
const DESCRIPTION_CARD_MAX_WIDTH = 460

type DescriptionGridLayout = {
  columnCount: number
  maxWidth: number
}

type AreasSelectorSettingsParams = {
  areasStyle: any
  areasCardsAlignment: any
  areasTextAlignment: any
  textFieldTheme: any
}

interface Props {
  label: string
  areaOptions: AreaOption[]
  selectedAreaId: string
  showDescription?: boolean
  dropdownTheme: DropdownTheme
  settingsParams: AreasSelectorSettingsParams
  onChange: (areaId: string) => void
  layoutSize?: LayoutSize
  className?: string
}

export const AreasSelector: FC<Props> = ({
  label,
  areaOptions,
  selectedAreaId,
  showDescription,
  dropdownTheme,
  settingsParams,
  onChange,
  layoutSize,
  className,
}) => {
  const settings = useSettings()
  const cardsRef = useRef<HTMLDivElement>(null)
  const [maxCardsHeight, setMaxCardsHeight] = useState<number>()
  const [descriptionGridLayout, setDescriptionGridLayout] = useState<DescriptionGridLayout>()

  const areasStyle = settings.get(settingsParams.areasStyle)
  const cardsAlignment = settings.get(settingsParams.areasCardsAlignment)
  const textAlignment = settings.get(settingsParams.areasTextAlignment)
  const textFieldTheme = settings.get(settingsParams.textFieldTheme)
  const labelThemeClass = textFieldTheme === TextFieldTheme.Box ? classes.box : classes.line
  const cardsAlignmentClass = alignmentClasses[cardsAlignment] ?? classes.left
  const textAlignmentClass = alignmentClasses[textAlignment] ?? classes.left
  const shouldShowDescriptions = showDescription === true
  const hasCardDescriptions = areaOptions.some(({description}) => shouldShowDescriptions && description)
  const isMobileLayout = layoutSize === LayoutSize.XS || layoutSize === LayoutSize.S
  const isCardsStyle = areasStyle !== AreasStyle.Dropdown
  const descriptionGridClass =
    !isMobileLayout && hasCardDescriptions ? classes.withDescriptions : undefined
  const singleColumnClass = isMobileLayout && hasCardDescriptions ? classes.singleColumn : undefined
  const limitedRowsClass = maxCardsHeight ? classes.limitedRows : undefined
  const cardsStyle = {
    ...(maxCardsHeight ? {maxHeight: maxCardsHeight} : {}),
    ...(descriptionGridLayout
      ? {
          gridTemplateColumns: `repeat(${descriptionGridLayout.columnCount}, minmax(0, 1fr))`,
          maxWidth: descriptionGridLayout.maxWidth,
        }
      : {}),
  }

  useEffect(() => {
    const cardsElement = cardsRef.current

    if (!areaOptions.length || !isCardsStyle || !cardsElement || isMobileLayout) {
      setMaxCardsHeight(undefined)
      setDescriptionGridLayout(undefined)
      return
    }

    const getGap = (property: 'rowGap' | 'columnGap') => {
      const computedStyle = window.getComputedStyle(cardsElement)
      const value = computedStyle[property] || computedStyle.gap

      return parseFloat(value) || 0
    }

    const updateLayout = () => {
      if (hasCardDescriptions) {
        const cardsWidth = cardsElement.clientWidth
        const columnGap = getGap('columnGap')
        const maxColumnCount = Math.max(
          1,
          Math.floor((cardsWidth + columnGap) / (DESCRIPTION_CARD_MIN_WIDTH + columnGap)),
        )
        const nextColumnCount = Math.min(areaOptions.length, maxColumnCount)
        const nextMaxWidth =
          nextColumnCount * DESCRIPTION_CARD_MAX_WIDTH + (nextColumnCount - 1) * columnGap

        setDescriptionGridLayout((currentGridLayout) =>
          currentGridLayout?.columnCount === nextColumnCount &&
          currentGridLayout.maxWidth === nextMaxWidth
            ? currentGridLayout
            : {columnCount: nextColumnCount, maxWidth: nextMaxWidth},
        )
      } else {
        setDescriptionGridLayout(undefined)
      }

      const cardElements = Array.from(cardsElement.querySelectorAll<HTMLElement>('button'))
      const rowTops = [...new Set(cardElements.map(({offsetTop}) => offsetTop))].sort((a, b) => a - b)

      if (rowTops.length <= 3) {
        setMaxCardsHeight(undefined)
        return
      }

      const nextMaxHeight = Math.ceil(rowTops[3] - rowTops[0] - getGap('rowGap'))

      setMaxCardsHeight((currentMaxHeight) =>
        currentMaxHeight === nextMaxHeight ? currentMaxHeight : nextMaxHeight,
      )
    }

    const animationFrame = window.requestAnimationFrame(updateLayout)
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updateLayout)

    resizeObserver?.observe(cardsElement)
    cardsElement
      .querySelectorAll<HTMLElement>('button')
      .forEach((cardElement) => resizeObserver?.observe(cardElement))
    window.addEventListener('resize', updateLayout)

    return () => {
      window.cancelAnimationFrame(animationFrame)
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updateLayout)
    }
  }, [areaOptions, cardsAlignment, hasCardDescriptions, isCardsStyle, isMobileLayout, textAlignment])

  if (!areaOptions.length) {
    return null
  }

  if (areasStyle === AreasStyle.Dropdown) {
    return (
      <Dropdown
        upgrade
        label={label}
        aria-label={label}
        className={className}
        initialSelectedId={selectedAreaId}
        options={getAreaDropdownOptions(areaOptions)}
        onChange={({id}) => id && onChange(id)}
        theme={dropdownTheme}
      />
    )
  }

  return (
    <fieldset className={st(classes.root, className)}>
      <legend className={st(classes.label, labelThemeClass)}>{label}</legend>
      <div
        ref={cardsRef}
        className={st(
          classes.cards,
          cardsAlignmentClass,
          descriptionGridClass,
          singleColumnClass,
          limitedRowsClass,
        )}
        style={Object.keys(cardsStyle).length ? cardsStyle : undefined}
      >
        {areaOptions.map(({id, name, description}) => {
          const selected = id === selectedAreaId
          const hasDescription = Boolean(shouldShowDescriptions && description)

          return (
            <button
              key={id}
              type="button"
              className={st(
                classes.card,
                {selected},
                hasCardDescriptions ? classes.withDescription : undefined,
                textAlignmentClass,
              )}
              aria-pressed={selected}
              data-selected={selected}
              onClick={() => onChange(id)}
              onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onChange(id)
                }
              }}
            >
              <span className={classes.title}>{name}</span>
              {hasDescription ? <span className={classes.description}>{description}</span> : null}
            </button>
          )
        })}
      </div>
    </fieldset>
  )
}
